import React from "react";
import {
  Box,
  Flex,
  InputGroup,
  Input,
  InputLeftAddon,
  Text,
  FormErrorMessage,
  FormControl,
  FormLabel,
  FormHelperText,
  HStack,
  Button,
  VStack,
  Stack,
  Spacer,
  Divider,
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Icon,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  ETradeAutoSyncControllerProps,
  useETradeAutoSyncController,
} from "./useETRADEAutoSyncController";
import { Show } from "../../../../components";
import ETradeAutoSyncInstructions from "../../../uploads/panels/BrokerUpload/instructions/EtradeAutoSyncInstructions";
import { CheckCircleIcon, WarningIcon } from "@chakra-ui/icons";

// Define the component with typed props
export const ETRADE_AutoSync: React.FC<ETradeAutoSyncControllerProps> = (
  props
) => {
  // Use custom controller hook for managing the form state
  const controller = useETradeAutoSyncController(props);

  // Colors for light/dark mode
  const cardBg = useColorModeValue("white", "gray.700");
  const stepBorderColor = useColorModeValue("gray.200", "gray.600");
  const stepActiveColor = useColorModeValue("green.500", "green.300");

  // Determine step states
  const step1Complete =
    !!controller.config.apiKey && !!controller.config.secretKey;
  const step2Active = step1Complete;
  const step2Complete = !!controller.config.code;
  const step3Active = step2Complete;
  const step3Complete =
    !!controller.config.accessToken ||
    controller.connectionStatus === "CONNECTED";

  // Update API key and secret fields
  const handleAPIKeyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updatedKey = e.target.value;
    controller.setConfig({ ...controller.config, apiKey: updatedKey });
    controller.saveConfig({ ...controller.config, apiKey: updatedKey });
  };

  const handleAPISecretChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updatedSecret = e.target.value;
    controller.setConfig({ ...controller.config, secretKey: updatedSecret });
    controller.saveConfig({ ...controller.config, secretKey: updatedSecret });
  };

  // Status indicator for steps
  const StepStatus = ({
    complete,
    active,
  }: {
    complete: boolean;
    active: boolean;
  }) => {
    if (complete) {
      return <Icon as={CheckCircleIcon} color="green.500" boxSize={5} />;
    }
    if (active) {
      return <Icon as={WarningIcon} color="orange.500" boxSize={5} />;
    }
    return null;
  };

  // Button text based on connection status
  const getConnectionButtonText = () => {
    if (controller.config.accessToken || controller.connectionStatus === "CONNECTED") {
      return "Connected ✓";
    }

    switch (controller.connectionStatus) {
      case "FAILED":
        return "Failed, Try Again";
      case "IN_PROGRESS":
        return "Connecting...";
      default:
        return "Test Connection";
    }
  };

  return (
    <VStack spacing={6} align="stretch" w="full">
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <Box>
          <AlertTitle fontWeight="bold">E*TRADE Auto Sync</AlertTitle>
          <AlertDescription>
            E*TRADE will allow us to update your account for 1 day. After that,
            you'll need to reconnect using the "Get Link Code" button.
          </AlertDescription>
        </Box>
      </Alert>

      {/* Step 1: API Keys */}
      <Card
        bg={cardBg}
        borderRadius="lg"
        borderWidth="1px"
        borderColor={step1Complete ? stepActiveColor : stepBorderColor}
        boxShadow={step1Complete ? "md" : "sm"}
      >
        <CardHeader pb={0}>
          <HStack>
            <Text fontWeight="bold" fontSize="lg">
              Step 1: Enter API Keys
            </Text>
            <Spacer />
            <StepStatus complete={step1Complete} active={!step1Complete} />
          </HStack>
        </CardHeader>
        <CardBody>
          <VStack align="stretch" spacing={4}>
            <Text fontSize="sm" color="gray.500">
              Enter your E*TRADE Consumer Key and API Secret from your developer
              account
            </Text>

            <FormControl isInvalid={!controller.validAPIKey}>
              <FormLabel>Consumer Key</FormLabel>
              <Input
                value={controller.config.apiKey || ""}
                onChange={handleAPIKeyChange}
                placeholder="Enter your E*TRADE Consumer Key"
                isReadOnly={!!controller.readOnly}
              />
              {!controller.validAPIKey && (
                <FormErrorMessage>Consumer Key is required</FormErrorMessage>
              )}
            </FormControl>

            <FormControl isInvalid={!controller.validAPISecret}>
              <FormLabel>API Secret</FormLabel>
              <Input
                value={controller.config.secretKey || ""}
                onChange={handleAPISecretChange}
                placeholder="Enter your E*TRADE API Secret"
                isReadOnly={!!controller.readOnly}
                type="password"
              />
              {!controller.validAPISecret && (
                <FormErrorMessage>API Secret is required</FormErrorMessage>
              )}
            </FormControl>
          </VStack>
        </CardBody>
      </Card>

      {/* Step 2: Generate Link Code */}
      <Card
        bg={cardBg}
        borderRadius="lg"
        borderWidth="1px"
        borderColor={
          step2Active
            ? step2Complete
              ? stepActiveColor
              : "orange.300"
            : stepBorderColor
        }
        boxShadow={step2Active ? "md" : "sm"}
        opacity={step2Active ? 1 : 0.7}
      >
        <CardHeader pb={0}>
          <HStack>
            <Text fontWeight="bold" fontSize="lg">
              Step 2: Generate Link Code
            </Text>
            <Spacer />
            <StepStatus
              complete={step2Complete}
              active={step2Active && !step2Complete}
            />
          </HStack>
        </CardHeader>
        <CardBody>
          <VStack align="stretch" spacing={4}>
            <Text fontSize="sm" color="gray.500">
              Generate a link code to authorize E*TRADE access
            </Text>

            <HStack>
              <Button
                colorScheme="green"
                isDisabled={
                  !controller.config.apiKey || !controller.config.secretKey
                }
                onClick={(e) => {
                  e.preventDefault();
                  controller.testConfig("AUTHORIZE_URL");
                }}
                size="md"
                width="200px"
              >
                Get Link Code
              </Button>
              <Text fontSize="sm" color="gray.500">
                This will open E*TRADE's authorization page
              </Text>
            </HStack>

            <FormControl>
              <FormLabel>Link Code</FormLabel>
              <Input
                value={controller.config.code || ""}
                onChange={(e) => controller.setLinkCode(e.target.value)}
                placeholder="Enter the link code from E*TRADE"
                isReadOnly={!!controller.readOnly}
              />
              <FormHelperText>
                Copy the code from E*TRADE and paste it here
              </FormHelperText>
            </FormControl>
          </VStack>
        </CardBody>
      </Card>

      {/* Step 3: Confirm Connection */}
      <Card
        bg={cardBg}
        borderRadius="lg"
        borderWidth="1px"
        borderColor={
          step3Active
            ? step3Complete
              ? stepActiveColor
              : "orange.300"
            : stepBorderColor
        }
        boxShadow={step3Active ? "md" : "sm"}
        opacity={step3Active ? 1 : 0.7}
      >
        <CardHeader pb={0}>
          <HStack>
            <Text fontWeight="bold" fontSize="lg">
              Step 3: Confirm Connection
            </Text>
            <Spacer />
            <StepStatus
              complete={step3Complete}
              active={step3Active && !step3Complete}
            />
          </HStack>
        </CardHeader>
        <CardBody>
          <VStack align="stretch" spacing={4}>
            <Text fontSize="sm" color="gray.500">
              Confirm your connection to E*TRADE to enable auto-sync
            </Text>

            <Button
              colorScheme={step3Complete ? "green" : "blue"}
              isDisabled={
                !controller.config.apiKey ||
                !controller.config.secretKey ||
                !controller.config.code ||
                controller.connectionStatus === "CONNECTED" ||
                controller.connectionStatus === "IN_PROGRESS"
              }
              onClick={(e) => {
                e.preventDefault();
                controller.testConfig("TEST_CONNECTION");
              }}
              size="md"
              width="200px"
            >
              {getConnectionButtonText()}
            </Button>

            {controller.connectionStatus === "CONNECTED" && (
              <Alert status="success" borderRadius="md">
                <AlertIcon />
                <AlertDescription>
                  Successfully connected to E*TRADE! Your account will now
                  automatically sync.
                </AlertDescription>
              </Alert>
            )}

            {controller.connectionStatus === "FAILED" && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertDescription>
                  Connection failed. Please check your link code and try again.
                </AlertDescription>
              </Alert>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Action Buttons */}
      <HStack justifyContent="flex-end" pt={4}>
        <Button
          variant="outline"
          onClick={() => {
            controller.setConfig({
              apiKey: "",
              secretKey: "",
              accessToken: "",
              accessTokenSecret: "",
              oauthToken: "",
              oauthTokenSecret: "",
              code: "",
            });
            controller.saveConfig({
              apiKey: "",
              secretKey: "",
              accessToken: "",
              accessTokenSecret: "",
              oauthToken: "",
              oauthTokenSecret: "",
              code: "",
            });
          }}
        >
          Reset
        </Button>

        {props.close && <Button onClick={props.close}>Close</Button>}
      </HStack>

      <Divider my={6} />

      {/* Instructions */}
      <Card bg={cardBg} borderRadius="lg" variant="outline">
        <CardHeader>
          <Heading size="md">E*TRADE Auto Sync Instructions</Heading>
        </CardHeader>
        <CardBody>
          <ETradeAutoSyncInstructions />
        </CardBody>
      </Card>
    </VStack>
  );
};
